import { z } from "zod";
import type { PageDoc } from "./pageDoc.js";
import type { Permission } from "./permissions.js";

/**
 * CT-RM-30: multilingual content.
 *
 * Every piece of org content (email/letter templates, landing pages, volunteer
 * opportunities, P2P campaigns) is written in the org's default language; staff
 * add human-written variants for other languages as string overrides
 * (content_translations). Nothing here translates text by machine.
 */

/** languages an org can write content in (native names are shown to visitors) */
export const LANGUAGES = {
  en: { name: "English", native: "English" },
  es: { name: "Spanish", native: "Español" },
  fr: { name: "French", native: "Français" },
  pt: { name: "Portuguese", native: "Português" },
  de: { name: "German", native: "Deutsch" },
  zh: { name: "Chinese", native: "中文" },
  vi: { name: "Vietnamese", native: "Tiếng Việt" },
  ko: { name: "Korean", native: "한국어" },
  tl: { name: "Tagalog", native: "Tagalog" },
  ar: { name: "Arabic", native: "العربية" },
  ru: { name: "Russian", native: "Русский" },
  ht: { name: "Haitian Creole", native: "Kreyòl ayisyen" },
} as const;
export type LanguageCode = keyof typeof LANGUAGES;
export const LANGUAGE_CODES = Object.keys(LANGUAGES) as [LanguageCode, ...LanguageCode[]];
export const zLanguage = z.enum(LANGUAGE_CODES);

/** languages with full app string catalogs (public chrome, receipts); others fall back to English chrome */
export const UI_LANGUAGES = ["en", "es"] as const satisfies readonly LanguageCode[];
export type UiLanguage = (typeof UI_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: LanguageCode = "en";

export const isUiLanguage = (code: string | null | undefined): code is UiLanguage => (UI_LANGUAGES as readonly string[]).includes(code ?? "");

/** "es-MX" / "ES" / " es " → "es"; unknown or malformed tags → null */
export function normalizeLanguage(tag: string | null | undefined): LanguageCode | null {
  if (typeof tag !== "string") return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0] ?? "";
  return primary in LANGUAGES ? (primary as LanguageCode) : null;
}

/**
 * Accept-Language → known primary languages, best first. Entries with q=0 are
 * refused; ties keep header order; duplicates (es-MX, es) collapse.
 */
export function parseAcceptLanguage(header: string | null | undefined): LanguageCode[] {
  if (!header || typeof header !== "string") return [];
  const ranked = header
    .slice(0, 1000)
    .split(",")
    .map((part, i) => {
      const [tag = "", ...params] = part.trim().split(";");
      const qParam = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
      const q = qParam ? Number(qParam.slice(2)) : 1;
      return { lang: normalizeLanguage(tag), q: Number.isFinite(q) ? q : 0, i };
    })
    .filter((e): e is { lang: LanguageCode; q: number; i: number } => e.lang !== null && e.q > 0)
    .sort((a, b) => b.q - a.q || a.i - b.i);
  return [...new Set(ranked.map((e) => e.lang))];
}

export interface LanguageRequest {
  /** an explicit choice: the ?lang= param or the language switcher */
  requested?: string | null;
  /** a known constituent's preferred language */
  preferred?: string | null;
  /** the browser's Accept-Language header */
  acceptLanguage?: string | null;
}

/**
 * Picks the language to serve: explicit ?lang= first, then the constituent's
 * preference, then Accept-Language — the first that is `available`. Otherwise
 * the fallback (the org default language, whose content always exists).
 */
export function resolveLanguage(req: LanguageRequest, available: readonly LanguageCode[], fallback: LanguageCode): LanguageCode {
  const candidates = [normalizeLanguage(req.requested), normalizeLanguage(req.preferred), ...parseAcceptLanguage(req.acceptLanguage)];
  for (const c of candidates) if (c && available.includes(c)) return c;
  return fallback;
}

// ---- org language settings (organizations.settings) ----

export const zOrgLanguageSettings = z
  .object({
    /** the language base content is written in; the fallback for every variant lookup */
    defaultLanguage: zLanguage,
    /** languages offered on public pages (always includes the default) */
    languages: z.array(zLanguage).min(1).max(LANGUAGE_CODES.length),
  })
  .refine((s) => s.languages.includes(s.defaultLanguage), { message: "The default language must be one of the offered languages", path: ["languages"] });
export type OrgLanguageSettings = z.infer<typeof zOrgLanguageSettings>;

/** reads the language settings out of organizations.settings, tolerating missing/old values */
export function orgLanguages(settings: unknown): OrgLanguageSettings {
  const s = (settings ?? {}) as { defaultLanguage?: unknown; languages?: unknown };
  const defaultLanguage = normalizeLanguage(typeof s.defaultLanguage === "string" ? s.defaultLanguage : null) ?? DEFAULT_LANGUAGE;
  const listed = Array.isArray(s.languages) ? s.languages.map((l) => normalizeLanguage(typeof l === "string" ? l : null)).filter((l): l is LanguageCode => !!l) : [];
  return { defaultLanguage, languages: [...new Set([defaultLanguage, ...listed])] };
}

/** language info every public page payload carries, for the language switcher */
export interface PublicLanguageInfo {
  /** the language the content (and UI chrome, when it has a catalog) is served in */
  language: LanguageCode;
  /** languages this page can be shown in, default first */
  languages: LanguageCode[];
}

/** GET /public/give/:org — the hosted giving page */
export interface PublicGivingPage extends PublicLanguageInfo {
  organizationName: string;
  currency: string;
  paymentMode: "checkout" | "simulated" | "unavailable";
  funds: { id: string; name: string }[];
}

// ---- locale-aware formatting ----

/** region used to localize number/date conventions, from the org's currency */
const CURRENCY_REGION: Record<string, string> = { USD: "US", CAD: "CA", GBP: "GB", AUD: "AU", NZD: "NZ", MXN: "MX", EUR: "", CHF: "CH", INR: "IN", ZAR: "ZA" };

/** BCP 47 locale for a language in an org's market: es + USD → es-US ($50.00), es + EUR → es (50,00 €) */
export function localeFor(language: string | null | undefined, currency?: string | null): string {
  const lang = normalizeLanguage(language) ?? DEFAULT_LANGUAGE;
  const region = currency ? CURRENCY_REGION[currency.toUpperCase()] : undefined;
  const tag = region ? `${lang}-${region}` : lang;
  try {
    return Intl.NumberFormat.supportedLocalesOf([tag]).length ? tag : lang;
  } catch {
    return lang;
  }
}

/** minor units → localized currency string ("$1,234.50", "1234,50 €") */
export function formatMoneyLocale(minor: number, currency: string, language: string | null | undefined, opts: { whole?: boolean } = {}): string {
  const code = (currency || "USD").toUpperCase();
  let digits = 2;
  try {
    digits = new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    /* unknown currency: assume 2 */
  }
  const value = minor / 10 ** digits;
  const fractions = opts.whole && Number.isInteger(value) ? 0 : digits;
  try {
    return new Intl.NumberFormat(localeFor(language, code), { style: "currency", currency: code, minimumFractionDigits: fractions, maximumFractionDigits: fractions }).format(value);
  } catch {
    return `${code} ${value.toFixed(fractions)}`;
  }
}

/** an ISO date (YYYY-MM-DD) or timestamp → localized long date ("4 de noviembre de 2026") */
export function formatDateLocale(iso: string | Date, language: string | null | undefined, opts: { style?: "long" | "medium" | "short"; currency?: string | null; timeZone?: string } = {}): string {
  const dateOnly = typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = typeof iso === "string" ? new Date(dateOnly ? `${iso}T12:00:00Z` : iso) : iso;
  if (Number.isNaN(d.getTime())) return String(iso);
  const timeZone = dateOnly ? "UTC" : opts.timeZone;
  try {
    return new Intl.DateTimeFormat(localeFor(language, opts.currency), { dateStyle: opts.style ?? "long", ...(timeZone ? { timeZone } : {}) }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

// ---- translatable content ----

export const TRANSLATABLE_ENTITIES = ["email_template", "letter_template", "landing_page", "volunteer_opportunity", "p2p_campaign"] as const;
export const zTranslatableEntity = z.enum(TRANSLATABLE_ENTITIES);
export type TranslatableEntity = z.infer<typeof zTranslatableEntity>;

/** server-side permission to read / write each entity type's translations (mirrors the entity's own routes) */
export const TRANSLATION_PERMISSIONS: Record<TranslatableEntity, { read: Permission; write: Permission }> = {
  email_template: { read: "email:manage_templates", write: "email:manage_templates" },
  letter_template: { read: "mail:send", write: "mail:manage" },
  landing_page: { read: "page:read", write: "page:manage" },
  volunteer_opportunity: { read: "volunteer:read", write: "volunteer:manage" },
  p2p_campaign: { read: "gift:read", write: "campaign:manage" },
};

/** one string staff can translate: its key in the variant, a label, and the default-language source */
export interface TranslatableString {
  key: string;
  label: string;
  /** the default-language text */
  source: string;
  multiline: boolean;
}

interface StaticField {
  key: string;
  label: string;
  multiline: boolean;
}

/** fixed translatable fields per entity type (landing pages are derived from the page document) */
export const TRANSLATABLE_FIELDS: Record<Exclude<TranslatableEntity, "landing_page">, StaticField[]> = {
  email_template: [
    { key: "subject", label: "Subject", multiline: false },
    { key: "bodyHtml", label: "Body (HTML)", multiline: true },
    { key: "bodyText", label: "Plain-text body", multiline: true },
  ],
  letter_template: [{ key: "bodyHtml", label: "Letter body (HTML)", multiline: true }],
  volunteer_opportunity: [
    { key: "name", label: "Name", multiline: false },
    { key: "description", label: "Description", multiline: true },
    { key: "location", label: "Location", multiline: false },
    { key: "requirements", label: "Requirements", multiline: true },
  ],
  p2p_campaign: [
    { key: "name", label: "Campaign name", multiline: false },
    { key: "description", label: "Description", multiline: true },
  ],
};

export const MAX_TRANSLATION_KEYS = 300;
export const zTranslationFields = z
  .record(z.string().min(1).max(120), z.string().max(50_000))
  .refine((r) => Object.keys(r).length <= MAX_TRANSLATION_KEYS, { message: `At most ${MAX_TRANSLATION_KEYS} strings per translation` });
export type TranslationFields = z.infer<typeof zTranslationFields>;

export const zUpsertTranslation = z.object({
  /** key → human-written text; blank strings fall back to the default language */
  fields: zTranslationFields,
});
export type UpsertTranslation = z.infer<typeof zUpsertTranslation>;

export const zContentTranslation = z.object({
  language: zLanguage,
  fields: z.record(z.string()),
  updatedAt: z.string(),
});
export type ContentTranslation = z.infer<typeof zContentTranslation>;

/** GET /translations/:entityType/:entityId */
export interface TranslationsView {
  entityType: TranslatableEntity;
  entityId: string;
  defaultLanguage: LanguageCode;
  /** languages the org offers, default first */
  languages: LanguageCode[];
  strings: TranslatableString[];
  translations: ContentTranslation[];
}

/** static-field source strings for an entity row */
export function staticTranslatableStrings(entityType: Exclude<TranslatableEntity, "landing_page">, row: Record<string, unknown>): TranslatableString[] {
  return TRANSLATABLE_FIELDS[entityType].map((f) => ({ ...f, source: typeof row[f.key] === "string" ? (row[f.key] as string) : "" }));
}

/** overlays non-blank translated strings onto the base fields (unknown keys are ignored) */
export function applyFieldTranslation<T extends object>(base: T, fields: Record<string, string> | null | undefined, keys: readonly string[]): T {
  if (!fields) return base;
  const out = { ...base } as Record<string, unknown>;
  for (const k of keys) {
    const v = fields[k];
    if (typeof v === "string" && v.trim()) out[k] = v;
  }
  return out as T;
}

/**
 * Landing pages: every visible string, keyed by block (and field/step) id so a
 * variant never changes structure — field ids, option values, amounts and
 * styles always come from the default-language page. Select option values are
 * not translatable because submissions are matched on them.
 */
export function pageTranslatableStrings(doc: PageDoc): TranslatableString[] {
  const out: TranslatableString[] = [];
  const add = (key: string, label: string, source: string, multiline = false) => {
    if (source.trim()) out.push({ key, label, source, multiline });
  };
  doc.blocks.forEach((b, i) => {
    const n = `Block ${i + 1}`;
    const k = (field: string) => `block.${b.id}.${field}`;
    switch (b.type) {
      case "hero":
        add(k("heading"), `${n} · hero heading`, b.heading);
        add(k("subheading"), `${n} · hero subheading`, b.subheading, true);
        break;
      case "heading":
        add(k("text"), `${n} · heading`, b.text);
        break;
      case "text":
        add(k("text"), `${n} · text`, b.text, true);
        break;
      case "image":
        add(k("alt"), `${n} · image description`, b.alt);
        break;
      case "button":
        add(k("label"), `${n} · button label`, b.label);
        break;
      case "stats":
        b.items.forEach((it, j) => add(k(`items.${j}.label`), `${n} · stat ${j + 1} label`, it.label));
        break;
      case "form":
        add(k("title"), `${n} · form title`, b.title);
        add(k("submitLabel"), `${n} · submit button`, b.submitLabel);
        add(k("successMessage"), `${n} · thank-you message`, b.successMessage, true);
        for (const f of b.fields) {
          add(`field.${b.id}.${f.id}.label`, `${n} · field “${f.label}”`, f.label);
          add(`field.${b.id}.${f.id}.placeholder`, `${n} · field “${f.label}” placeholder`, f.placeholder);
        }
        break;
      case "giving":
        add(k("title"), `${n} · giving title`, b.title);
        add(k("buttonLabel"), `${n} · giving button`, b.buttonLabel);
        break;
      case "progress":
        add(k("title"), `${n} · progress title`, b.title);
        break;
      case "donor_wall":
        add(k("title"), `${n} · donor wall title`, b.title);
        break;
      default:
        break;
    }
  });
  for (const s of doc.steps ?? []) add(`step.${s.id}.title`, `Step “${s.title}” title`, s.title);
  return out;
}

/** the page with a variant's strings applied — same blocks, ids and values; only text changes */
export function applyPageTranslation(doc: PageDoc, fields: Record<string, string> | null | undefined): PageDoc {
  if (!fields || !Object.keys(fields).length) return doc;
  const t = (key: string, fallback: string) => {
    const v = fields[key];
    return typeof v === "string" && v.trim() ? v : fallback;
  };
  const blocks = doc.blocks.map((b) => {
    const k = (field: string) => `block.${b.id}.${field}`;
    switch (b.type) {
      case "hero":
        return { ...b, heading: t(k("heading"), b.heading), subheading: t(k("subheading"), b.subheading) };
      case "heading":
      case "text":
        return { ...b, text: t(k("text"), b.text) };
      case "image":
        return { ...b, alt: t(k("alt"), b.alt) };
      case "button":
        return { ...b, label: t(k("label"), b.label) };
      case "stats":
        return { ...b, items: b.items.map((it, j) => ({ ...it, label: t(k(`items.${j}.label`), it.label) })) };
      case "form":
        return {
          ...b,
          title: t(k("title"), b.title),
          submitLabel: t(k("submitLabel"), b.submitLabel),
          successMessage: t(k("successMessage"), b.successMessage),
          fields: b.fields.map((f) => ({ ...f, label: t(`field.${b.id}.${f.id}.label`, f.label), placeholder: t(`field.${b.id}.${f.id}.placeholder`, f.placeholder) })),
        };
      case "giving":
        return { ...b, title: t(k("title"), b.title), buttonLabel: t(k("buttonLabel"), b.buttonLabel) };
      case "progress":
      case "donor_wall":
        return { ...b, title: t(k("title"), b.title) };
      default:
        return b;
    }
  }) as PageDoc["blocks"];
  return { ...doc, blocks, steps: (doc.steps ?? []).map((s) => ({ ...s, title: t(`step.${s.id}.title`, s.title) })) };
}

// ---- built-in constituent-facing copy (human-written; never machine translated) ----

export interface ReceiptCopy {
  officialReceipt: string;
  yearEndStatement: (year: number) => string;
  receiptIssued: (receiptNumber: string, issued: string) => string;
  date: string;
  designation: string;
  amount: string;
  deductible: string;
  total: string;
  disclaimer: string;
  emailSubject: (orgName: string, receiptNumber: string) => string;
  emailPreheader: (receiptNumber: string) => string;
  emailHeading: string;
  emailBody: (receiptNumber: string, amount: string) => string;
  /** default acknowledgment when an ack rule has no template */
  ackSubject: string;
  ackBodyHtml: string;
  /** letter {{salutation}} */
  salutation: (firstName: string) => string;
  donorFallback: string;
  /** CT-RM-31: combined (cart) receipt line labels */
  oneTime: string;
  monthly: string;
  feeCover: string;
}

export const RECEIPT_COPY: Record<UiLanguage, ReceiptCopy> = {
  en: {
    officialReceipt: "Official Gift Receipt",
    yearEndStatement: (year) => `Year-End Giving Statement — ${year}`,
    receiptIssued: (n, issued) => `Receipt ${n} · Issued ${issued}`,
    date: "Date",
    designation: "Designation",
    amount: "Amount",
    deductible: "Deductible",
    total: "Total",
    disclaimer:
      "No goods or services were provided in exchange for this contribution except as noted. " +
      "Amounts shown as deductible reflect the value of any benefits received. " +
      "Please retain this receipt for your tax records.",
    emailSubject: (org, n) => `Your receipt from ${org} (${n})`,
    emailPreheader: (n) => `Your official receipt ${n} is attached.`,
    emailHeading: "Thank you for your gift",
    emailBody: (n, amount) => `Your official receipt ${n} for ${amount} is attached as a PDF. Please keep it for your records.`,
    ackSubject: "Thank you for your gift",
    ackBodyHtml: "<p>Dear {{firstName}},</p><p>Thank you for your generous gift of {{amount}}. It makes our work possible.</p>",
    salutation: (first) => `Dear ${first},`,
    donorFallback: "Donor",
    oneTime: "One-time",
    monthly: "Monthly",
    feeCover: "Includes processing fees you covered",
  },
  es: {
    officialReceipt: "Recibo oficial de donación",
    yearEndStatement: (year) => `Resumen anual de donaciones — ${year}`,
    receiptIssued: (n, issued) => `Recibo ${n} · Emitido el ${issued}`,
    date: "Fecha",
    designation: "Destino",
    amount: "Importe",
    deductible: "Deducible",
    total: "Total",
    disclaimer:
      "No se entregaron bienes ni servicios a cambio de esta contribución, salvo que se indique lo contrario. " +
      "Los importes deducibles reflejan el valor de cualquier beneficio recibido. " +
      "Conserve este recibo para su declaración de impuestos.",
    emailSubject: (org, n) => `Su recibo de ${org} (${n})`,
    emailPreheader: (n) => `Adjuntamos su recibo oficial ${n}.`,
    emailHeading: "Gracias por su donación",
    emailBody: (n, amount) => `Adjuntamos en PDF su recibo oficial ${n} por ${amount}. Le recomendamos guardarlo para sus registros.`,
    ackSubject: "Gracias por su donación",
    ackBodyHtml: "<p>Estimado/a {{firstName}}:</p><p>Muchas gracias por su generosa donación de {{amount}}. Gracias a usted, nuestro trabajo es posible.</p>",
    salutation: (first) => `Estimado/a ${first}:`,
    donorFallback: "Donante",
    oneTime: "Única",
    monthly: "Mensual",
    feeCover: "Incluye las comisiones de procesamiento que usted cubrió",
  },
};

/** built-in copy for a language: its own catalog, else the org default's, else English */
export function receiptCopy(language: string | null | undefined, orgDefault?: string | null): ReceiptCopy {
  const lang = normalizeLanguage(language);
  if (isUiLanguage(lang)) return RECEIPT_COPY[lang];
  const fallback = normalizeLanguage(orgDefault);
  return isUiLanguage(fallback) ? RECEIPT_COPY[fallback] : RECEIPT_COPY.en;
}
